import { Node } from '@tiptap/core'
import { VueNodeViewRenderer } from '@tiptap/vue-3'
import RawHtmlNodeView from './RawHtmlNodeView.vue'

/**
 * SECURITY: this node stores and renders arbitrary HTML/CSS verbatim, with no
 * sanitization at any layer — not here, not in the backend (ProductController
 * writes it straight to the content->* JSON column), not in the public product
 * page (Blade renders it with {!! !!}, unescaped). That's the whole point of
 * this node (the "no rails" escape hatch for cases the other custom nodes
 * can't cover), but it also means it's an XSS vector for anyone who can type
 * into this editor. Currently the insert button is visible to every editor
 * user with no role/permission gate. If this editor is ever opened up to
 * lower-trust users (e.g. academy staff instead of just admins), gate this
 * node/button behind a permission check before that happens.
 */
export const RawHtmlNode = Node.create({
  name: 'rawHtml',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      html: {
        default: '',
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="raw-html"]',
        getAttrs: element => ({ html: element.innerHTML }),
      },
    ]
  },

  renderHTML({ node }) {
    // Deliberately NOT the usual [tag, attrs, ...children] DOMOutputSpec array —
    // that would route node.attrs.html through document.createTextNode, which
    // HTML-escapes it. Returning a real DOM node with .innerHTML set is how
    // ProseMirror's DOMOutputSpec supports verbatim, unescaped markup.
    const wrapper = document.createElement('div')
    wrapper.setAttribute('data-type', 'raw-html')
    wrapper.innerHTML = node.attrs.html || ''
    return wrapper
  },

  addNodeView() {
    return VueNodeViewRenderer(RawHtmlNodeView)
  },

  addCommands() {
    return {
      insertRawHtml: () => ({ chain }) => {
        return chain()
          .insertContent({
            type: this.name,
          })
          .run()
      },
    }
  },
})

export default RawHtmlNode
